import type {
	PublicBookingCreateRequestDto,
	PublicBookingCreateResponseDto,
	PublicBookingDto,
	PublicBookingErrorCode,
	PublicBookingErrorDto,
	PublicBookingPageDto,
	PublicBookingPersonDto,
	PublicBookingTimesDto,
	PublicBoothTargetDto,
} from '@supply-mover-2-0/shared';
import { fetchOrNetworkError } from '../lib/portalNetworkError';
import { getPortalTenantHeader } from '../lib/tenantResolver';

/**
 * PD-17646 — the guest booking endpoints. Guests have no login, so these can't use
 * `portalFetch`, which adds an Authorization header; the tenant comes from the page's
 * address, as for the public forms.
 */

export class BookingRequestError extends Error {
	public constructor(
		public readonly status: number,
		public readonly code: PublicBookingErrorCode,
		message: string,
		public readonly nearest: Array<string>,
		public readonly field: string | null,
	) {
		super(message);
	}
}

function headers(base: Record<string, string> = {}): HeadersInit {
	const tenantHeader = getPortalTenantHeader();

	return tenantHeader != null ? { ...base, 'X-Portal-Tenant': tenantHeader } : base;
}

const BASE = `${import.meta.env.VITE_API_URL}/api/portal/booking`;

async function read<TData>(response: Response): Promise<TData> {
	if (response.ok) {
		return ((await response.json()) as { data: TData }).data;
	}

	const body = (await response.json().catch(() => null)) as Partial<PublicBookingErrorDto> | null;

	throw new BookingRequestError(
		response.status,
		body?.code ?? 'unavailable',
		body?.message ?? 'Something went wrong. Please try again.',
		body?.nearest ?? [],
		body?.field ?? null,
	);
}

async function get<TData>(path: string, query: Record<string, string | null> = {}): Promise<TData> {
	const params = new URLSearchParams();

	for (const [key, value] of Object.entries(query)) {
		if (value != null) {
			params.set(key, value);
		}
	}

	const search = params.toString();
	const url = `${BASE}/${path}${search !== '' ? `?${search}` : ''}`;

	return read<TData>(await fetchOrNetworkError(url, { headers: headers() }, 'crm-api'));
}

async function post<TData>(path: string, body: unknown): Promise<TData> {
	return read<TData>(
		await fetchOrNetworkError(
			`${BASE}/${path}`,
			{
				method: 'POST',
				headers: headers({ 'Content-Type': 'application/json' }),
				body: JSON.stringify(body),
			},
			'crm-api',
		),
	);
}

const part = encodeURIComponent;

/** PD-17649 — where a lasting booth link goes today. */
export function fetchBoothTarget(kit: string | null): Promise<PublicBoothTargetDto> {
	return get(kit != null ? `booth/${part(kit)}` : 'booth');
}

export function fetchBookingPerson(person: string): Promise<PublicBookingPersonDto> {
	return get(`person/${part(person)}`);
}

export function fetchBookingPage(
	person: string,
	type: string,
	inviteToken: string | null,
): Promise<PublicBookingPageDto> {
	return get(`person/${part(person)}/${part(type)}`, { t: inviteToken });
}

export async function fetchBookingTimes(
	person: string,
	type: string,
	window: { from: string; to: string },
	inviteToken: string | null,
): Promise<Array<string>> {
	const times = await get<PublicBookingTimesDto>(`person/${part(person)}/${part(type)}/times`, {
		...window,
		t: inviteToken,
	});

	return times.starts;
}

export function createBooking(
	person: string,
	type: string,
	body: PublicBookingCreateRequestDto,
): Promise<PublicBookingCreateResponseDto> {
	return post(`person/${part(person)}/${part(type)}/book`, body);
}

export function fetchBooking(token: string): Promise<PublicBookingDto> {
	return get(`meeting/${part(token)}`);
}

export async function fetchRescheduleTimes(
	token: string,
	window: { from: string; to: string },
): Promise<Array<string>> {
	return (await get<PublicBookingTimesDto>(`meeting/${part(token)}/times`, window)).starts;
}

export function rescheduleBooking(
	token: string,
	body: { start: string; timezone: string; turnstileToken: string | null },
): Promise<PublicBookingDto> {
	return post(`meeting/${part(token)}/reschedule`, body);
}

export function cancelBooking(
	token: string,
	body: { turnstileToken: string | null },
): Promise<PublicBookingDto> {
	return post(`meeting/${part(token)}/cancel`, body);
}
